import {map, filter} from 'lodash';
import {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {CopyText, Icon, InformationModal} from 'components';
import {AnyParticipant, OnlineSession} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {ParticipantRole} from 'types';

import {Participant} from '../Participant';

const T_KEYS = TRANSLATION_KEYS.COMMON.SESSION.LABELS;

type Props = {
    open: boolean;
    onClose: () => void;
    session?: OnlineSession;
};

export const ParticipantModal: React.FC<Props> = ({open, session, onClose}: Props) => {
    const {t} = useTranslation();

    const getTopic = useCallback((participant: AnyParticipant) =>
        participant.role === ParticipantRole.Client && participant.data.userInfo.topic, []);

    const clients = useMemo(() => filter(session?.participants, {role: ParticipantRole.Client}), [session]);
    const singleClientTopic = useMemo(() => clients.length === 1 ? getTopic(clients[0]) : null, [clients, getTopic]);

    if (!session) {
        return null;
    }

    return (
        <InformationModal
            open={open}
            title={
                <div className='tw-flex tw-items-center tw-ts-base tw-font-normal tw-mr-3 tw-mt-1'>
                    <Icon name='id' size='lg' className='tw-mr-1' />
                    <CopyText
                        text={session.id}
                        labelPlacement='left'
                        iconColor='default-dimmed'
                    />
                </div>
            }
            maxWidth='sm'
            fullWidth
            onClose={onClose}
        >
            <div>
                {singleClientTopic &&
                    <div className='tw-mb-4'>
                        <div className='tw-ts-h5 tw-mb-1'>{t(T_KEYS.TOPIC)}</div>
                        <div>{singleClientTopic}</div>
                    </div>}
                <div>
                    <span className='tw-ts-h5 tw-mr-1'>{t(T_KEYS.PARTICIPANTS)}</span>
                    <span className='tw-text-default-dimmed tw-ml-0.5'>
                        {`(${session.participants.length})`}
                    </span>
                </div>
                {map(session.participants, (participant) => {
                    const topic = singleClientTopic ? null : getTopic(participant);
                    return (
                        <div key={participant.id} className='tw-mt-2'>
                            <Participant
                                participant={participant}
                                session={session}
                            />
                            {topic &&
                                <div className='tw-mb-6'>
                                    <div className='tw-ts-base-bold tw-mb-1'>{t(T_KEYS.TOPIC)}</div>
                                    <div>{topic}</div>
                                </div>}
                        </div>);
                })}
            </div>
        </InformationModal>
    );
};
